import { useEffect, useState } from "react";
import { api } from "../api.js";
import { card, btnPrimary, btnSecondary, inputStyle, selectStyle, labelStyle } from "../components/ui.js";

interface Announcement {
  id: string;
  type: "update" | "maintenance" | "incident" | "news";
  title: string;
  body: string;
  linkUrl: string | null;
  isImportant: boolean;
  notifyBell: boolean;
  publishedAt: string | null;
  expiresAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

const TYPE_LABEL: Record<string, string> = {
  update: "アップデート",
  maintenance: "メンテナンス",
  incident: "障害",
  news: "お知らせ",
};

const TYPE_COLOR: Record<string, { bg: string; fg: string }> = {
  update: { bg: "#EEEDFE", fg: "#534AB7" },
  maintenance: { bg: "#FFF3E0", fg: "#B86E00" },
  incident: { bg: "#FCEBEB", fg: "#C62828" },
  news: { bg: "#E8F5E9", fg: "#1D9E75" },
};

function formatDate(s: string | null | undefined): string {
  if (!s) return "-";
  return new Date(s).toLocaleDateString("ja-JP");
}

/** ISO 文字列 → datetime-local 入力用（"YYYY-MM-DDTHH:mm"） */
function toDatetimeLocal(s: string | null): string {
  if (!s) return "";
  const d = new Date(s);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type FormState = {
  type: Announcement["type"];
  title: string;
  body: string;
  linkUrl: string;
  isImportant: boolean;
  notifyBell: boolean;
  publishedAt: string;
  expiresAt: string;
};

const EMPTY_FORM: FormState = {
  type: "update",
  title: "",
  body: "",
  linkUrl: "",
  isImportant: false,
  notifyBell: false,
  publishedAt: "",
  expiresAt: "",
};

export function AdminAnnouncementsTab() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // 編集モーダルの状態（null = 閉じている、"new" = 新規作成、ID = その ID を編集）
  const [modalMode, setModalMode] = useState<null | "new" | string>(null);

  const load = async () => {
    try {
      const r = await api.adminListAnnouncements();
      setItems(r.announcements || []);
      setListError(null);
    } catch (e: any) {
      setListError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("このお知らせを削除しますか？既読データも削除されます。")) return;
    try {
      await api.adminDeleteAnnouncement(id);
      await load();
    } catch (e: any) {
      setListError(e.message);
    }
  };

  if (loading) return <div style={{ color: "#6b6b68" }}>読み込み中...</div>;

  const editingItem: Announcement | null =
    modalMode && modalMode !== "new"
      ? items.find((i) => i.id === modalMode) ?? null
      : null;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600 }}>お知らせ管理</div>
        <button onClick={() => setModalMode("new")} style={btnPrimary}>
          ＋ 新規作成
        </button>
      </div>

      {listError && (
        <div style={{ background: "#FCEBEB", color: "#C62828", padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {listError}
        </div>
      )}

      {/* 一覧 */}
      <div>
        {items.map((item) => {
          const c = TYPE_COLOR[item.type];
          return (
            <div key={item.id} style={{ ...card, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{
                      background: c.bg, color: c.fg, padding: "3px 10px",
                      borderRadius: 12, fontSize: 12, fontWeight: 600,
                    }}>
                      {TYPE_LABEL[item.type]}
                    </span>
                    {item.isImportant && (
                      <span style={{
                        background: "#FCEBEB", color: "#C62828", padding: "3px 10px",
                        borderRadius: 12, fontSize: 12, fontWeight: 600,
                      }}>重要</span>
                    )}
                    {item.notifyBell && (
                      <span style={{
                        background: "#FFF3E0", color: "#B86E00", padding: "3px 10px",
                        borderRadius: 12, fontSize: 12, fontWeight: 600,
                      }}>🔔 ベル</span>
                    )}
                    <span style={{ fontSize: 12, color: "#6b6b68" }}>
                      {formatDate(item.publishedAt || item.createdAt)}
                      {item.expiresAt && ` 〜 ${formatDate(item.expiresAt)}`}
                    </span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{item.title}</div>
                  <div style={{ fontSize: 13, color: "#6b6b68", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {item.body}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => setModalMode(item.id)}
                    style={{ ...btnSecondary, padding: "6px 14px", fontSize: 13 }}
                  >
                    編集
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    style={{
                      ...btnSecondary,
                      padding: "6px 14px",
                      fontSize: 13,
                      color: "#C62828",
                      borderColor: "#f0cccc",
                    }}
                  >
                    削除
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div style={{ ...card, textAlign: "center", color: "#6b6b68" }}>
            お知らせはありません。上部の「新規作成」から作成してください。
          </div>
        )}
      </div>

      {/* 作成・編集モーダル */}
      {modalMode && (
        <AnnouncementModal
          editing={editingItem}
          onClose={() => setModalMode(null)}
          onSaved={async () => {
            setModalMode(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/**
 * 作成・編集モーダル
 */
function AnnouncementModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: Announcement | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(() =>
    editing
      ? {
          type: editing.type,
          title: editing.title,
          body: editing.body,
          linkUrl: editing.linkUrl || "",
          isImportant: editing.isImportant,
          notifyBell: editing.notifyBell,
          publishedAt: toDatetimeLocal(editing.publishedAt),
          expiresAt: toDatetimeLocal(editing.expiresAt),
        }
      : EMPTY_FORM,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        title: form.title.trim(),
        body: form.body.trim(),
        linkUrl: form.linkUrl.trim() || null,
        isImportant: form.isImportant,
        notifyBell: form.notifyBell,
        publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : null,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
      };
      if (editing) {
        await api.adminUpdateAnnouncement(editing.id, payload);
      } else {
        await api.adminCreateAnnouncement(payload);
      }
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          background: "white",
          borderRadius: 12,
          padding: 28,
          width: "100%",
          maxWidth: 720,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {editing ? "お知らせを編集" : "お知らせを新規作成"}
          </div>
          <button type="button" onClick={onClose} style={{ ...btnSecondary, padding: "6px 14px" }}>
            閉じる
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <div style={labelStyle}>タイプ</div>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as Announcement["type"] })}
              style={selectStyle}
            >
              <option value="update">アップデート</option>
              <option value="maintenance">メンテナンス</option>
              <option value="incident">障害</option>
              <option value="news">お知らせ</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <Toggle
                checked={form.isImportant}
                onChange={(v) => setForm({ ...form, isImportant: v })}
              />
              重要
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <Toggle
                checked={form.notifyBell}
                onChange={(v) => setForm({ ...form, notifyBell: v })}
              />
              ベル通知
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>タイトル</div>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="例: AIレコメンド機能を改善しました"
            style={inputStyle}
            maxLength={255}
            required
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>本文</div>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="詳細内容を入力"
            style={{ ...inputStyle, minHeight: 120, resize: "vertical", fontFamily: "inherit" }}
            required
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>リンクURL（任意）</div>
          <input
            type="url"
            value={form.linkUrl}
            onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
            placeholder="https://..."
            style={inputStyle}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <div style={labelStyle}>公開日時（空欄で即公開）</div>
            <input
              type="datetime-local"
              value={form.publishedAt}
              onChange={(e) => setForm({ ...form, publishedAt: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>有効期限（空欄で無期限）</div>
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              style={inputStyle}
            />
          </div>
        </div>

        {error && (
          <div style={{ background: "#FCEBEB", color: "#C62828", padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
            キャンセル
          </button>
          <button type="submit" style={btnPrimary} disabled={saving}>
            {saving ? "保存中..." : editing ? "更新する" : "作成する"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <span
      onClick={() => onChange(!checked)}
      style={{
        display: "inline-block",
        width: 36,
        height: 20,
        borderRadius: 10,
        background: checked ? "#534AB7" : "#d0cfca",
        position: "relative",
        cursor: "pointer",
        transition: "background 0.2s",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "white",
          transition: "left 0.2s",
        }}
      />
    </span>
  );
}
